import { useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Cache em memória compartilhado entre componentes
let _cache = null;
let _pending = null;
const _subscribers = new Set();

const _emit = () => {
  _subscribers.forEach((cb) => cb(_cache));
};

export const fetchSubcategories = async ({ force = false } = {}) => {
  if (_cache && !force) return _cache;
  if (_pending && !force) return _pending;
  _pending = axios
    .get(`${API_URL}/subcategories`)
    .then((res) => {
      _cache = res.data || [];
      _pending = null;
      _emit();
      return _cache;
    })
    .catch((err) => {
      _pending = null;
      throw err;
    });
  return _pending;
};

export const invalidateSubcategories = async () => {
  await fetchSubcategories({ force: true });
};

export const useSubcategories = () => {
  const [items, setItems] = useState(_cache || []);
  const [loading, setLoading] = useState(!_cache);

  useEffect(() => {
    const cb = (data) => setItems(data || []);
    _subscribers.add(cb);
    if (!_cache) {
      fetchSubcategories()
        .then((data) => {
          setItems(data);
          setLoading(false);
        })
        .catch(() => setLoading(false));
    } else {
      setLoading(false);
    }
    return () => _subscribers.delete(cb);
  }, []);

  // Memoiza grouped para não recriar objeto a cada render (evita loops em consumers com useEffect).
  const grouped = useMemo(
    () =>
      items.reduce((acc, sub) => {
        (acc[sub.parent] = acc[sub.parent] || []).push(sub);
        return acc;
      }, {}),
    [items]
  );

  const getName = useCallback(
    (slug) => items.find((s) => s.slug === slug)?.name || slug,
    [items]
  );

  const getItem = useCallback(
    (slug) => items.find((s) => s.slug === slug),
    [items]
  );

  const refresh = useCallback(async () => {
    setLoading(true);
    const data = await fetchSubcategories({ force: true });
    setItems(data);
    setLoading(false);
    return data;
  }, []);

  return { items, grouped, loading, getName, getItem, refresh };
};
